// Who may set another account's password from a settings form, and what the
// box starts out holding. Shared by the two user forms that carry the field —
// setting/user/AddUser.jsx (/setting/List-user) and
// setting/Student/AddStudentUser.jsx (/student-users) — so the rule is
// answered in one place rather than copied into each dialog.

/* ── The gate ───────────────────────────────────────────────────────────
   Setting a password on someone else's account is an admin action, so the
   field is not rendered at all for anyone else. The same shape the rest of
   the app uses to ask this (AddEventForm.jsx, ListEvent.jsx): every profile
   is checked, not profiles[0] -- an account can hold more than one role, and
   reading index 0 alone hides the field from an admin whose Admin profile
   happens not to be first in the list.

   Every signal the app is known to carry admin-ness on is accepted, because
   which ones the /me response actually populates varies: isSuperAdmin (the
   common one), a `role` of SuperAdmin (Chat.jsx reads this), a profile
   labelled admin, and the bare `profileIds: 'admin'` string that
   AuthContext's own hasPermission treats as full access. A label is matched
   as a substring so "Admin", "Super Admin" and "Branch Admin" all count --
   a stricter equality check here is invisible in the UI and reads as the
   field being broken.
   ---------------------------------------------------------------------- */
export const canSetPassword = (user) => {
  if (!user) return false;

  const truthyFlag = (v) => v === true || v === "true" || v === 1 || v === "1";
  if (truthyFlag(user.isSuperAdmin) || truthyFlag(user.isAdmin)) return true;

  const role = String(user.role || "").toLowerCase().replace(/[\s_-]/g, "");
  if (role === "superadmin" || role === "admin") return true;

  if (String(user.profileIds || "").toLowerCase() === "admin") return true;

  return (user.profiles || []).some((p) =>
    String(p?.label || "").toLowerCase().includes("admin")
  );
};

/* ── DEV_MODE ───────────────────────────────────────────────────────────
   Read as a literal member expression, never process.env[name]: this is a
   Create React App build (react-scripts 5), and CRA inlines env vars by
   substituting the exact text `process.env.REACT_APP_FOO` at build time. A
   computed lookup finds nothing in the browser, where process.env is only
   the handful of keys that were inlined.

   REACT_APP_DEV_MODE is the key that actually reaches the bundle -- CRA
   passes through nothing else. DEV_MODE is read as well so that a plain
   DEV_MODE=true works if it is ever wired in through a webpack define, but
   on its own, in .env, it will not be visible here.
   ---------------------------------------------------------------------- */
const truthy = (v) => ["true", "1", "yes", "on"].includes(String(v ?? "").trim().toLowerCase());

export const isDevMode = () =>
  truthy(process.env.REACT_APP_DEV_MODE) || truthy(process.env.DEV_MODE);

/* What the forms actually ask, and the one predicate behind both the field
   and the payload: if the box is on screen, saving it has to work. Declared
   under isDevMode, which it reads.

   DEV_MODE opens the field to whoever is signed in. The flag only exists on
   a developer's own machine, it is the switch that fills the box with the
   real password in the first place, and a dev account that is not flagged
   admin in the database would otherwise turn the whole feature into an
   empty space with no explanation for why it is not there. */
export const mayEditPassword = (user) => isDevMode() || canSetPassword(user);

/* A stored password is a hash, and a hash is not something to put in the
   box: it would be shown as though it were the password, and saving the
   form would post it back to be hashed a second time -- which sets the
   account's password to the hash of its own hash and locks it out. Covers
   the $2a$/$2b$/$2y$ bcrypt prefixes and the $argon2…$/$scrypt$ family, plus
   a bare 32-to-128-character hex digest (md5/sha1/sha256/sha512). */
const looksHashed = (value) =>
  /^\$(2[abxy]?|argon2(i|d|id)?|scrypt|pbkdf2[^$]*|6|5|1)\$/i.test(value) ||
  /^[a-f0-9]{32,128}$/i.test(value);

/* ── The prefill ────────────────────────────────────────────────────────
   What the password box should start with on an edit.

   Normally "" -- blank, meaning "leave the password alone". The field
   cannot show the current password (the API stores a hash), so an empty box
   must not read as "no password"; handleSubmit drops the key entirely when
   nothing was typed.

   Under DEV_MODE, if the record came back carrying a readable password, it
   is put in the box so the account can be signed into while developing.
   Anything hash-shaped is refused even then, for the reason above.

   As things stand this always returns "", and that is not a bug to go
   hunting for: auth-api hashes the password on the way in (user.service.js
   `add` and `update`, bcrypt cost 12) and never keeps the plaintext, and
   GET /users/:id (`view`) selects an explicit column list that does not
   include `password`. There is nothing to prefill. The function stays
   because it is also the guard that keeps a hash out of the box if that
   select ever grows one -- showing a hash as though it were the password
   is how the account gets locked out. Prefilling for real would mean
   storing a plaintext copy server-side, which was considered and declined.
   ---------------------------------------------------------------------- */
export const prefillPassword = (record) => {
  if (!isDevMode()) return "";
  const stored = String(record?.password ?? record?.plainPassword ?? "").trim();
  if (!stored || looksHashed(stored)) return "";
  return stored;
};
